import { type CSSProperties, type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { useEscapeLayer } from './escape';
import { computePosition, type Placement, Portal, type Rect, rectOf } from './layer';

export type PopoverProps = {
  open: boolean;
  onClose: () => void;
  /** Елемент-якір або віртуальний прямокутник (контекстне меню в точці курсора). */
  anchor: HTMLElement | Rect | null;
  placement?: Placement;
  offset?: number;
  /** Мінімальна ширина — як у якоря. */
  matchWidth?: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
  role?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Куди поставити фокус після відкриття: 'first' — перший інтерактивний елемент, ref — конкретний. */
  initialFocus?: 'first' | 'none' | RefObject<HTMLElement | null>;
  /** Повернути фокус на якір після закриття. */
  restoreFocus?: boolean;
  /** Не закривати при кліку по цих елементах (наприклад, поле введення комбінованого списку). */
  ignoreOutside?: (target: Node) => boolean;
};

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Popover({
  open,
  onClose,
  anchor,
  placement = 'bottom-start',
  offset = 6,
  matchWidth,
  className,
  style,
  children,
  role = 'dialog',
  id,
  initialFocus = 'first',
  restoreFocus = true,
  ignoreOutside,
  ...aria
}: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; origin: string; minWidth?: number } | null>(null);
  const [order, setOrder] = useState(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const close = useCallback(() => onCloseRef.current(), []);

  useEscapeLayer(open, close);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el || !anchor) return;
    const a = anchor instanceof HTMLElement ? rectOf(anchor) : anchor;
    const minWidth = matchWidth ? a.width : undefined;
    const width = Math.max(el.offsetWidth, minWidth ?? 0);
    const p = computePosition(a, { width, height: el.offsetHeight }, placement, offset);
    setPos((prev) =>
      prev && prev.top === p.top && prev.left === p.left && prev.minWidth === minWidth ? prev : { top: p.top, left: p.left, origin: p.origin, minWidth },
    );
  }, [anchor, matchWidth, placement, offset]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    setOrder(nextLayerOrder());
  }, [open]);

  useLayoutEffect(() => {
    if (open) update();
  }, [open, update]);

  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    const ro = el ? new ResizeObserver(() => update()) : null;
    if (el && ro) ro.observe(el);
    const onScroll = (e: Event) => {
      if (el && e.target instanceof Node && el.contains(e.target)) return;
      update();
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, update]);

  // Закриття кліком поза елементом.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (anchor instanceof HTMLElement && anchor.contains(t)) return;
      if (ignoreOutside?.(t)) return;
      // Клік у шарі, відкритому пізніше (вкладене меню, діалог) — не закриваємо.
      const layer = t instanceof Element ? (t.closest('[data-ers-layer]') as HTMLElement | null) : null;
      if (layer && Number(layer.dataset.ersOrder ?? 0) > Number(ref.current?.dataset.ersOrder ?? 0)) return;
      close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open, anchor, close, ignoreOutside]);

  // Фокус після відкриття й повернення після закриття.
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      if (initialFocus === 'none') return;
      if (initialFocus === 'first') {
        const first = el.querySelector<HTMLElement>('[data-autofocus]') ?? el.querySelector<HTMLElement>(FOCUSABLE);
        (first ?? el).focus({ preventScroll: true });
      } else initialFocus.current?.focus({ preventScroll: true });
    }, 0);
    return () => {
      window.clearTimeout(t);
      if (!restoreFocus) return;
      const active = document.activeElement;
      const focusWasInside = !active || active === document.body || ref.current?.contains(active);
      if (focusWasInside) {
        const target = anchor instanceof HTMLElement ? anchor : prev;
        target?.focus?.({ preventScroll: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <Portal>
      <div
        ref={ref}
        id={id}
        role={role}
        tabIndex={role === 'presentation' || role === 'none' ? undefined : -1}
        data-ers-layer=""
        data-ers-order={order}
        {...aria}
        className={cn(
          'ers-pop fixed z-50 rounded-md border border-line bg-raised text-fg shadow-pop outline-none',
          !pos && 'invisible',
          className,
        )}
        style={{
          top: pos?.top ?? 0,
          left: pos?.left ?? 0,
          minWidth: pos?.minWidth,
          ['--ers-origin' as string]: pos?.origin,
          ...style,
        }}
      >
        {children}
      </div>
    </Portal>
  );
}

let layerSeq = 0;
/** Порядковий номер відкриття шару: пізніше відкритий шар «вищий». */
export function nextLayerOrder() {
  layerSeq += 1;
  return layerSeq;
}
